import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	TextField,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { EntityType, FormData } from '../types';
import NoteColorPickerButton from './NoteColorPickerButton';
import { handleNoteTitleKeyDown } from './noteDialogUtils';

const LONG_NOTE_LINE_BREAK_THRESHOLD = 3;
const LONG_NOTE_CHAR_THRESHOLD = 100;

function countLineBreaks(text: string): number {
	return (text.match(/\n/g) ?? []).length;
}

function isLongNoteContent(text: string): boolean {
	return (
		countLineBreaks(text) >= LONG_NOTE_LINE_BREAK_THRESHOLD
		|| text.length >= LONG_NOTE_CHAR_THRESHOLD
	);
}

interface DialogProps {
	open: boolean;
	onClose: () => void;
	onSubmit: (formData: FormData) => void;
	elementSelectSlot?: React.ReactNode;
}

interface NoteFormValues {
	title: string;
	description: string;
}

const getInitialValues = (): NoteFormValues => ({
	title: '',
	description: '',
});

const AddNoteDialog: React.FC<DialogProps> = ({
	open,
	onClose,
	onSubmit,
	elementSelectSlot,
}) => {
	const [values, setValues] = useState<NoteFormValues>(getInitialValues);
	const [isLongMode, setIsLongMode] = useState(false);
	const [color, setColor] = useState<string | null>(null);

	useEffect(() => {
		if (open) {
			setValues(getInitialValues());
			setIsLongMode(false);
			setColor(null);
		}
	}, [open]);

	const handleChange = (patch: Partial<NoteFormValues>) => {
		setValues((prev) => ({
			...prev,
			...patch,
		}));
	};

	const handleDialogClose = () => {
		setValues(getInitialValues());
		setColor(null);
		onClose();
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();

		const rawTitle = values.title.trim();

		if (!rawTitle) {
			return;
		}

		const isHeaderShortcut = !isLongMode && rawTitle.endsWith(':');
		const title = isHeaderShortcut ? rawTitle.slice(0, -1).trim() : rawTitle;

		if (!title) {
			return;
		}

		const payload: FormData = {
			type: EntityType.Note,
			title,
			...(isHeaderShortcut
				? { variant: 'header' as const }
				: isLongMode
					? { variant: 'long' as const }
					: {}),
			...(color ? { color } : {}),
		};

		onSubmit(payload);
	};

	const handlePaste = useCallback(
		(event: React.ClipboardEvent) => {
			const pasted = event.clipboardData.getData('text');
			if (!pasted) {
				return;
			}
			const input = event.target as HTMLInputElement | HTMLTextAreaElement;
			const start = input.selectionStart ?? values.title.length;
			const end = input.selectionEnd ?? values.title.length;
			const nextTitle = values.title.slice(0, start) + pasted + values.title.slice(end);
			if (isLongNoteContent(pasted) || isLongNoteContent(nextTitle)) {
				setIsLongMode(true);
			}
		},
		[values.title],
	);

	const handleTitleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			handleNoteTitleKeyDown(event, {
				isLong: isLongMode,
				onPromoteToLong: () => setIsLongMode(true),
			});
		},
		[isLongMode],
	);

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			maxWidth={isLongMode ? 'sm' : 'xs'}
			fullWidth
			slotProps={{
				paper: {
					sx: color
						? {
							bgcolor: (theme) => alpha(color, theme.palette.mode === 'dark' ? 0.22 : 0.14),
							transition: 'background-color 0.2s ease',
						}
						: undefined,
				},
			}}
		>
			<Box
				component='form'
				onSubmit={handleSubmit}
				autoComplete='off'
				sx={{ display: 'contents' }}
			>
				{elementSelectSlot != null && (
					<Box display="flex" justifyContent="flex-end" height={48} mb={-3} alignItems="center">
						<Box pr={2}>{elementSelectSlot}</Box>
					</Box>
				)}
				<DialogContent>
					<Box display='flex' flexDirection='column' gap={2} mt={elementSelectSlot != null ? 0 : 2}>
						<TextField
							onChange={(event) => handleChange({ title: event.target.value === ' ' ? '' : event.target.value })}
							value={values.title}
							variant='standard'
							fullWidth
							required
							autoFocus
							multiline={isLongMode}
							minRows={isLongMode ? 4 : undefined}
							placeholder='Add a note...'
							slotProps={{ input: { onPaste: handlePaste, onKeyDown: handleTitleKeyDown } }}
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'space-between' }}>
					<NoteColorPickerButton value={color} onChange={setColor} />
					<Box display='flex' gap={1}>
						<Button type='button' onClick={handleDialogClose}>
							Discard
						</Button>
						<Button type='submit' variant='contained'>
							Save note
						</Button>
					</Box>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default AddNoteDialog;
